import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check, CreditCard, Wallet, Banknote, ArrowLeftRight, FileMinus } from 'lucide-react';
import { fetchRefundCapacity, RefundCapacity } from '../../lib/refunds';
import type { PickableDestination } from './RefundDestinationPicker';

/**
 * Compact destination control for a refund that is already decided: one
 * pill showing the current choice, a dropdown to change it. Sits in a sticky
 * footer / totals row. Card shows its cap; unavailable options are greyed
 * with the reason.
 */
export interface RefundDestinationSelectProps {
  businessId: string;
  customerId?: string | null;
  invoiceId?: string | null;
  amountCents: number;
  value: PickableDestination;
  onChange: (next: PickableDestination) => void;
  destinations?: PickableDestination[];
  /** Cash / e-transfer reference (Interac ref). */
  reference?: string;
  onReferenceChange?: (next: string) => void;
  /** Where the menu opens. Footers open upward. */
  direction?: 'up' | 'down';
  /** Label override for 'invoice' (e.g. "Credit on this sale"). */
  invoiceLabel?: string;
}

const fmt = (c: number) => `$${(c / 100).toFixed(2)}`;

const META: Record<PickableDestination, { label: string; Icon: React.ComponentType<{ className?: string }> }> = {
  card: { label: 'Back to card', Icon: CreditCard },
  wallet: { label: 'Wallet credit', Icon: Wallet },
  invoice: { label: 'Credit on this invoice', Icon: FileMinus },
  cash: { label: 'Cash', Icon: Banknote },
  e_transfer: { label: 'E-transfer', Icon: ArrowLeftRight },
};

export default function RefundDestinationSelect({
  businessId,
  customerId,
  invoiceId,
  amountCents,
  value,
  onChange,
  destinations = ['wallet', 'card', 'cash', 'e_transfer'],
  reference = '',
  onReferenceChange,
  direction = 'up',
  invoiceLabel,
}: RefundDestinationSelectProps) {
  const [cap, setCap] = useState<RefundCapacity | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCap(null);
    fetchRefundCapacity({ businessId, customerId, invoiceId })
      .then(c => { if (!cancelled) setCap(c); })
      .catch(() => { if (!cancelled) setCap({ cardCents: 0, cardPayments: 0, stripeConnected: false, walletCents: 0 }); });
    return () => { cancelled = true; };
  }, [businessId, customerId, invoiceId]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  const cardCap = cap?.cardCents ?? 0;
  const reasonFor = (d: PickableDestination): string | null => {
    if (d === 'card') {
      if (!cap) return null;
      if (!cap.stripeConnected) return 'Stripe is not connected';
      if (cardCap <= 0) return customerId ? 'no card payments left to refund against' : 'add the customer first';
      return null;
    }
    if (d === 'wallet' && !customerId) return 'add the customer first';
    return null;
  };

  // If the current choice becomes unavailable, move to the first usable one.
  useEffect(() => {
    if (!cap) return;
    if (reasonFor(value)) {
      const next = destinations.find(d => !reasonFor(d));
      if (next && next !== value) onChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cap, customerId]);

  const labelFor = (d: PickableDestination) => (d === 'invoice' && invoiceLabel ? invoiceLabel : META[d].label);
  const Current = META[value].Icon;
  const split = value === 'card' && cap && cardCap > 0 && amountCents > cardCap;

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-semibold text-emerald-700">
          Deposit refund {fmt(amountCents)}
        </span>
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="inline-flex h-8 items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 pl-2.5 pr-2 text-[12px] font-semibold text-emerald-800 hover:bg-emerald-100 touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <Current className="h-3.5 w-3.5" />
          {labelFor(value)}
          {value === 'card' && cap && cardCap > 0 && (
            <span className="font-normal text-emerald-700/70">· up to {fmt(cardCap)}</span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {split && (
        <p className="mt-0.5 text-[11px] text-gray-500">
          {fmt(cardCap)} back to card; the remaining {fmt(amountCents - cardCap)} becomes wallet credit.
        </p>
      )}
      {value === 'e_transfer' && onReferenceChange && (
        <input
          type="text"
          value={reference}
          onChange={e => onReferenceChange(e.target.value)}
          placeholder="Interac reference (optional)"
          className="mt-1.5 h-8 w-full rounded-lg border border-gray-200 px-2.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        />
      )}

      {open && (
        <div
          className={`absolute right-0 z-30 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ${
            direction === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          }`}
        >
          {destinations.map(d => {
            const reason = reasonFor(d);
            const disabled = Boolean(reason);
            const Icon = META[d].Icon;
            return (
              <button
                key={d}
                type="button"
                disabled={disabled}
                onClick={() => { onChange(d); setOpen(false); }}
                className={`flex w-full items-start gap-2 px-3 py-2 text-left text-[12px] ${
                  disabled ? 'cursor-not-allowed text-gray-400' : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="font-semibold">
                    {labelFor(d)}
                    {d === 'card' && cap && !reason && (
                      <span className="font-normal text-gray-500"> — up to {fmt(cardCap)}</span>
                    )}
                  </span>
                  {reason && <span className="block text-[11px] text-gray-400">{reason}</span>}
                </span>
                {value === d && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
